import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toApiReportType } from "services/reportSubscriptions";

const useAssetActions = ({
	assetData,
	getEditableData,
	setSelectedRow,
	setEditData,
	setModal,
}) => {
	const { t } = useTranslation();
	const [activeReportRequest, setActiveReportRequest] = useState(null);
	const closeReportDialog = useCallback(() => setActiveReportRequest(null), []);

	const findRow = useCallback((rowId) => {
		const row = assetData.data.find((item) => item.id === rowId);
		if (row) return row;
		if (assetData.childData) {
			for (const children of Object.values(assetData.childData)) {
				const child = children.find((item) => item.id === rowId);
				if (child) return child;
			}
		}
		return null;
	}, [assetData.data, assetData.childData]);

	const handleEdit = useCallback((rowId) => {
		const row = findRow(rowId);
		if (row) {
			setSelectedRow(row);
			setEditData(getEditableData(row));
			setModal((prev) => ({ ...prev, edit: true }));
		}
	}, [findRow, getEditableData, setSelectedRow, setEditData, setModal]);

	const handleDelete = useCallback((rowId) => {
		const row = findRow(rowId);
		if (row) {
			setSelectedRow(row);
			setModal((prev) => ({ ...prev, delete: true }));
		}
	}, [findRow, setSelectedRow, setModal]);

	const handleAdd = useCallback(() => {
		setModal((prev) => ({ ...prev, add: true }));
	}, [setModal]);

	const handleImport = useCallback(() => {
		setModal((prev) => ({ ...prev, import: true }));
	}, [setModal]);

	const handleDownloadReport = useCallback((rowId) => {
		const row = findRow(rowId);
		const assetLabel = row?.assetNumber || row?.reference || `#${rowId}`;
		setActiveReportRequest({
			reportName: t("label.tasksWhereNameUsed", { name: assetLabel }),
			body: {
				reportType: toApiReportType("tasksWhereAssetUsed"),
				filterContext: { siteAssetRegisterID: rowId },
				name: null,
				recipientEmail: null,
				sendEmail: false,
			},
		});
	}, [findRow, t]);

	const closeModal = useCallback((modalType) => {
		setModal((prev) => ({ ...prev, [modalType]: false }));
		setSelectedRow(null);
		setEditData({});
	}, [setModal, setSelectedRow, setEditData]);

	const refreshData = useCallback(async () => {
		await assetData.refreshData();
	}, [assetData]);

	return {
		findRow,
		handleEdit,
		handleDelete,
		handleAdd,
		handleImport,
		handleDownloadReport,
		closeModal,
		refreshData,
		activeReportRequest,
		closeReportDialog,
	};
};

export default useAssetActions;
